import type { AuditFork, Vertical, OpsCanvasStep, EffortTag, FunnelType, Quadrant, RoiInputRow, Seat } from '@vibecontrol/shared/roleforge';
import type { StakeholderInviteSlots } from './roleforge-invite-limits';

const _API_ORIGIN =
  import.meta.env.VITE_VIBECONTROL_API_URL ?? 'https://api.vibecontrol.app';

const API_BASE = _API_ORIGIN + '/api/roleforge';
const SKILLS_API_BASE = _API_ORIGIN + '/api/skills';

export interface WorkspaceBootstrapOrganization {
  id: string;
  name: string;
  plan?: string | null;
  preactivation_sponsor_id?: string | null;
  preactivation_activated_at?: string | null;
  preactivation_expired_at?: string | null;
}

export interface WorkspaceBootstrapWorkspace {
  id: string;
  name: string;
  organization_id: string;
}

export interface WorkspaceBootstrapProfile {
  display_name: string | null;
  first_name: string | null;
  last_name: string | null;
  avatar_url: string | null;
}

export interface WorkspaceBootstrap {
  organizations: WorkspaceBootstrapOrganization[];
  allWorkspacesByOrg: Record<string, WorkspaceBootstrapWorkspace[]>;
  profile: WorkspaceBootstrapProfile | null;
}

export type AuditStatus =
  | 'draft'
  | 'interviewing'
  | 'synthesizing'
  | 'validating'
  | 'ready_to_materialize'
  | 'materializing'
  | 'materialized'
  | 'failed'
  | 'archived';

export interface AuditRow {
  id: string;
  workspace_id: string;
  organization_id: string;
  audit_fork: AuditFork;
  vertical: Vertical | null;
  status: AuditStatus;
  summary: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
  parent_audit_id: string | null;
}

/** Mirrors the `roleforge_audit_ops_canvas` table row. Plan 3b-ii. */
export interface OpsCanvasEngineRow {
  id: string;
  audit_id: string;
  engine: string;
  name: string;
  description: string | null;
  steps: OpsCanvasStep[];
  position: number;
  created_at: string;
  updated_at: string;
}

/** Mirrors the `roleforge_audit_opportunities` table row. Plan 3b-ii. */
export interface Opportunity {
  id: string;
  audit_id: string;
  name: string;
  description: string | null;
  engine: string;
  effort: EffortTag;
  business_impact: number;
  ease_score: number;
  quadrant: Quadrant;
  candidate_class?: 'standard' | 'foundation' | 'gap';
  funnel_type: FunnelType | null;
  asset_type: string | null;
  applies: boolean;
  source: string;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

/**
 * DB row from `roleforge_audit_roi_inputs`. Extends the shared `RoiInputRow`
 * with DB-specific fields. `computeRoi(rows)` from @vibecontrol/shared/roleforge
 * accepts `RoiInputRow[]`, and this type satisfies that constraint.
 */
export interface RoiInputRowDb extends RoiInputRow {
  audit_id: string;
  position?: number;
  notes: string | null;
  metadata: Record<string, unknown>;
  annual_salary_cents?: number;
  created_at: string;
  updated_at: string;
}

/**
 * Mirrors the `roleforge_audit_recommendations` table row. Plan 3b-ii. Also
 * used as `AuditTeamRecommendation` (materialize-api.ts) — the two were
 * identical hand-copies until Task 4 (#1738) collapsed them to this one
 * definition; import and alias rather than redeclaring.
 *
 * Built on the shared `Seat` type. The v2 seat fields (department/level/
 * human/ai/public_persona) are declared below by hand — a new `Seat` field
 * does NOT arrive here automatically; add it to the override block — but
 * each one's payload type is anchored via `Seat['field']` indexed access,
 * so shape changes propagate. Uses `Pick` (not `Omit`) for the fields taken
 * whole from `Seat`: `Pick`'s keys are constrained to `keyof Seat`, so a
 * rename/removal in `Seat` errors here, whereas an `Omit` list silently
 * keeps unmatched keys. department/level/human/ai are required on `Seat`
 * (public_persona is optional there) but all five are NULL-able DB columns
 * (20260809163231_roleforge_v2_seats.sql), so v1-era rows without them still
 * typecheck — re-declared optional/nullable here rather than picked.
 */
export type RecommendationRow = Pick<Seat, 'name' | 'position'> & {
  id: string;
  audit_id: string;
  emoji: string;
  role_template_id?: string | null;
  bespoke_role?: Record<string, unknown> | null;
  tools: Array<Record<string, unknown>>;
  customizations: Record<string, unknown>;
  source: string;
  created_at: string;
  updated_at: string;
  department?: Seat['department'] | null;
  level?: Seat['level'] | null;
  human?: Seat['human'] | null;
  ai?: Seat['ai'] | null;
  public_persona?: Seat['public_persona'] | null;
};

/** Mirrors the `roleforge_audit_messages` table row. Plan 3b-ii. */
export interface Message {
  id: string;
  audit_id: string;
  role: 'system' | 'agent' | 'user' | 'tool';
  content: string;
  tool_call_id: string | null;
  tool_name: string | null;
  metadata: Record<string, unknown>;
  position: number;
  created_at: string;
}

/** Workspace-default agent identity used to render RoleForge messages. */
export interface RoleforgeAgentProfile {
  id: string;
  name: string;
  avatar_url: string | null;
}

export class ApiError extends Error {
  constructor(
    public readonly code: string,
    public readonly status: number,
    message?: string,
    /** Present on 409 synthesis guard responses. */
    public readonly suggestion?: string,
    /**
     * Issue #1324: the whole error body, so entitlement responses can carry
     * real numbers (`used`, `cap`, `cap_kind`, `scope`) through to the copy the
     * customer reads instead of being reduced to a code.
     */
    public readonly details?: Record<string, unknown>,
  ) {
    super(message ?? code);
    this.name = 'ApiError';
  }
}

async function call<T>(path: string, jwt: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      'Authorization': `Bearer ${jwt}`,
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(
      body.code ?? 'http_error',
      res.status,
      body.error ?? res.statusText,
      body.suggestion,
      body && typeof body === 'object' ? (body as Record<string, unknown>) : undefined,
    );
  }
  return res.json() as Promise<T>;
}

export async function getWorkspaceBootstrap(jwt: string): Promise<WorkspaceBootstrap> {
  const res = await fetch(`${_API_ORIGIN}/api/web/bootstrap`, {
    headers: {
      'Authorization': `Bearer ${jwt}`,
      'Content-Type': 'application/json',
    },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(
      body.code ?? 'bootstrap_failed',
      res.status,
      body.error ?? res.statusText,
    );
  }
  return res.json() as Promise<WorkspaceBootstrap>;
}

type ChildResource = 'ops-canvas' | 'opportunities' | 'roi-inputs' | 'recommendations';

export async function patchChild<T>(
  jwt: string,
  auditId: string,
  resource: ChildResource,
  rowId: string,
  patch: Partial<T>,
): Promise<{ row: T }> {
  return call(`/audits/${auditId}/${resource}/${rowId}`, jwt, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

export async function createChild<T>(
  jwt: string,
  auditId: string,
  resource: ChildResource,
  body: Partial<T>,
): Promise<{ row: T }> {
  return call(`/audits/${auditId}/${resource}`, jwt, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function deleteChild(
  jwt: string,
  auditId: string,
  resource: ChildResource,
  rowId: string,
): Promise<void> {
  const res = await fetch(`${API_BASE}/audits/${auditId}/${resource}/${rowId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${jwt}`,
      'Content-Type': 'application/json',
    },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(
      body.code ?? 'http_error',
      res.status,
      body.error ?? res.statusText,
      body.suggestion,
    );
  }
}

export async function createAudit(
  jwt: string,
  input: { workspace_id: string; audit_fork: AuditFork },
): Promise<{ audit: AuditRow; created: boolean; resumed: boolean }> {
  return call('/audits', jwt, { method: 'POST', body: JSON.stringify(input) });
}

export async function getCurrentAudit(
  jwt: string,
  workspaceId: string,
): Promise<AuditRow | null> {
  try {
    const result = await call<{ audit: AuditRow }>(
      `/audits/current?workspace_id=${encodeURIComponent(workspaceId)}`,
      jwt,
    );
    return result.audit;
  } catch (err) {
    if (err instanceof ApiError && err.code === 'audit_not_found' && err.status === 404) {
      return null;
    }
    throw err;
  }
}

export async function getAudit(
  jwt: string,
  id: string,
): Promise<{
  audit: AuditRow;
  agent: RoleforgeAgentProfile | null;
  messages: Message[];
  ops_canvas: OpsCanvasEngineRow[];
  opportunities: Opportunity[];
  roi_inputs: RoiInputRowDb[];
  recommendations: RecommendationRow[];
}> {
  return call(`/audits/${id}`, jwt);
}

export async function listAudits(
  jwt: string,
  workspaceId: string,
): Promise<{ audits: AuditRow[] }> {
  return call(`/audits?workspace_id=${encodeURIComponent(workspaceId)}`, jwt);
}

export async function updateAudit(
  jwt: string,
  id: string,
  patch: Partial<Pick<AuditRow, 'status' | 'summary' | 'metadata' | 'vertical'>>,
): Promise<{ audit: AuditRow }> {
  return call(`/audits/${id}`, jwt, { method: 'PATCH', body: JSON.stringify(patch) });
}

export interface ChangeProposal {
  id: string;
  audit_id: string;
  organization_id: string;
  workspace_id: string;
  title: string;
  summary: string;
  rationale: string | null;
  affected_artifacts: Array<Record<string, unknown>>;
  task_title: string;
  task_description: string;
  status: 'proposed' | 'task_created' | 'started' | 'cancelled';
  task_id: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export async function listChangeProposals(
  jwt: string,
  auditId: string,
): Promise<{ proposals: ChangeProposal[] }> {
  return call(`/audits/${auditId}/change-proposals`, jwt);
}

export async function approveChangeProposal(
  jwt: string,
  auditId: string,
  proposalId: string,
  mode: 'later' | 'now',
): Promise<{ proposal: ChangeProposal; task: Record<string, unknown>; thread?: Record<string, unknown>; route_status: string }> {
  return call(`/audits/${auditId}/change-proposals/${proposalId}/approve`, jwt, {
    method: 'POST',
    body: JSON.stringify({ mode }),
  });
}

// ─── Stakeholder Invitations ────────────────────────────────────────────────

/**
 * Issue #1304: there is no `token` field. The server stores only `sha256(token)`
 * and cannot reproduce the link after the invite is created, so the plaintext
 * arrives exactly once — as `respond_url` on the create response — and never on
 * a list read.
 */
export interface InvitationRow {
  id: string;
  audit_id: string;
  recipient_email: string;
  role_hint: string | null;
  template_id: string;
  invited_by: string;
  invited_at: string;
  accepted_at: string | null;
  completed_at: string | null;
}

/**
 * Create/reissue response. `respond_url` is the one-time copy of the invite
 * link: show it, let the owner copy it, and do not expect to see it again. A
 * server older than #1304 omits it.
 */
export interface CreatedInvitation {
  invitation: InvitationRow;
  respond_url?: string;
}

/**
 * Issue #1324 — the org-wide, lifetime invite slot truth, straight from the
 * server. `slots` is absent only when talking to a server older than #1324;
 * callers must render "unknown" in that case rather than falling back to a
 * per-audit count.
 */
export async function listInvitations(
  jwt: string,
  auditId: string,
): Promise<{ invitations: InvitationRow[]; slots?: StakeholderInviteSlots }> {
  return call(`/audits/${auditId}/invitations`, jwt);
}

export async function createInvitation(
  jwt: string,
  auditId: string,
  body: { email: string; role_hint?: string },
): Promise<CreatedInvitation> {
  return call(`/audits/${auditId}/invitations`, jwt, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * Plan 3e-ii Task 7.2 — alias of `createInvitation` that tags the request
 * body's `metadata.source = 'inline_nudge'` for analytics. `metadata` itself is
 * still not persisted by the create endpoint — the point is to mark the call
 * site. `note`, however, IS persisted (to
 * `roleforge_audit_invitations.metadata.note`) and quoted in the invitation
 * email since issue #1324; it used to be dropped by the route.
 */
export async function inviteTeammateInline(
  jwt: string,
  auditId: string,
  body: { email: string; role_hint?: string; note?: string },
): Promise<CreatedInvitation> {
  return call(`/audits/${auditId}/invitations`, jwt, {
    method: 'POST',
    body: JSON.stringify({ ...body, metadata: { source: 'inline_nudge' } }),
  });
}

/**
 * Issue #1374 — resend response. `respond_url` carries the ROTATED link, which
 * is the only way this client can hold a working URL for an invitation it did
 * not create in the current page visit (#1304 left the server unable to
 * reproduce one). A server older than #1374 answers `{ ok: true }` with no
 * `respond_url`; callers must treat the link as still unavailable in that case
 * rather than inventing one.
 */
export interface ResentInvitation {
  ok?: boolean;
  respond_url?: string;
}

/**
 * Re-mails the invitation. This ROTATES the token: the previously-sent link
 * stops working immediately, so every call site must warn the owner before
 * invoking it.
 */
export async function resendInvitation(
  jwt: string,
  auditId: string,
  inviteId: string,
): Promise<ResentInvitation> {
  return call(`/audits/${auditId}/invitations/${inviteId}/resend`, jwt, {
    method: 'POST',
  });
}

export async function deleteInvitation(
  jwt: string,
  auditId: string,
  inviteId: string,
): Promise<void> {
  const res = await fetch(`${API_BASE}/audits/${auditId}/invitations/${inviteId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${jwt}`,
      'Content-Type': 'application/json',
    },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(
      (body as Record<string, string>).code ?? 'http_error',
      res.status,
      (body as Record<string, string>).error ?? res.statusText,
    );
  }
}

// ─── Export Bundle ──────────────────────────────────────────────────────────

/**
 * Downloads the audit's export bundle (zip) and returns the blob plus the
 * filename parsed from the Content-Disposition header. Falls back to a
 * deterministic filename if the header is missing or unparseable.
 */
export async function downloadExportBundle(
  jwt: string,
  auditId: string,
): Promise<{ blob: Blob; filename: string }> {
  const res = await fetch(`${API_BASE}/audits/${auditId}/export-bundle`, {
    headers: { Authorization: `Bearer ${jwt}` },
  });
  if (!res.ok) {
    throw new ApiError('export_bundle_failed', res.status, `export bundle failed: ${res.status}`);
  }
  const disp = res.headers.get('Content-Disposition') ?? '';
  const match = disp.match(/filename="([^"]+)"/);
  const filename = match?.[1] ?? `roleforge-audit-${auditId}.zip`;
  const blob = await res.blob();
  return { blob, filename };
}

// ─── Course Entitlement (v2 delivery) ───────────────────────────────────────

/**
 * Plan 2 (v2 delivery) — DIY Implementation Course entitlement.
 *
 * Contract shared with Task 3: `GET /api/roleforge/course-entitlement`
 * returns `{ entitled: boolean }` for the authed user. The server endpoint
 * lands in Task 3; until then this 404s and callers must render a neutral
 * fallback (the delivery page treats any failure as "entitlement unknown",
 * never as "not entitled").
 */
export async function getCourseEntitlement(
  jwt: string,
): Promise<{ entitled: boolean }> {
  return call('/course-entitlement', jwt);
}

// ─── Late Trial Consent (v2 delivery re-offer) ─────────────────────────────

/**
 * Task 4 — the "not really an OTO" path. Records the free-upgrade consent for
 * the signed-in purchaser from the delivery page, days after purchase: the
 * Supabase session is the proof, so no PaymentIntent secret and no freshness
 * window. `POST /api/roleforge/trial-consent-account` (authed).
 *
 * `already: true` means a live consent row already existed (idempotent
 * no-op). Failures worth distinguishing at the call site (ApiError.status):
 * 409 = the consent was revoked — no self-serve re-consent, calm
 * contact-support copy; 403 = no roleforge purchase on record.
 */
export async function postTrialConsentAccount(
  jwt: string,
): Promise<{ consented: true; already: boolean }> {
  return call('/trial-consent-account', jwt, { method: 'POST' });
}

// ─── Skill Catalog ──────────────────────────────────────────────────────────

/** Mirrors the SkillCatalogEntry shape returned by GET /api/skills/catalog. */
export interface SkillCatalogEntry {
  /** Stable template identifier (e.g. "email-assistant"). */
  template_id: string;
  name: string;
  description: string;
  icon?: string;
  categories?: string[];
  skill_type: 'role' | 'tool';
  responsibilities?: string[];
  recommended_tools?: string[];
  metadata?: Record<string, unknown>;
  source_type?: string;
}

// ─── Stakeholder Shell (cookie-auth reads) ─────────────────────────────────

/**
 * Phase 10 — fetches the messages for a single thread. Used by the
 * RespondShell to seed the transcript on mount and re-pull after each
 * stream turn. Auth via the sb-stakeholder-session cookie (stakeholders)
 * or the regular Bearer header (owners reading transcripts) — credentials
 * are included so the browser carries either.
 */
export async function getThreadMessages(
  threadId: string,
): Promise<{ messages: Message[] }> {
  const res = await fetch(
    `${_API_ORIGIN}/api/roleforge/threads/${encodeURIComponent(threadId)}/messages`,
    { credentials: 'include' },
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(
      (body as Record<string, string>).code ?? 'http_error',
      res.status,
      (body as Record<string, string>).error ?? res.statusText,
    );
  }
  return res.json() as Promise<{ messages: Message[] }>;
}

/**
 * Issue #1250 — the stakeholder's own "I'm finished" confirmation. Moves the
 * thread to `completed_self` and stamps the interview snapshot. Cookie auth:
 * the server requires the caller to BE the thread's auth_user_id. Idempotent,
 * so a retry after a flaky network is safe.
 */
export async function completeOwnThread(
  threadId: string,
): Promise<{ ok: true; status: 'completed_self' }> {
  const res = await fetch(
    `${_API_ORIGIN}/api/roleforge/threads/${encodeURIComponent(threadId)}/complete`,
    { method: 'POST', credentials: 'include' },
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(
      (body as Record<string, string>).code ?? 'http_error',
      res.status,
      (body as Record<string, string>).error ?? res.statusText,
    );
  }
  return res.json() as Promise<{ ok: true; status: 'completed_self' }>;
}

/**
 * Phase 10 — minimal display info for the stakeholder welcome card. The
 * authenticated cookie's auth_user_id MUST match the invitation's
 * auth_user_id; otherwise the server replies 403.
 */
export async function getInvitationMeta(
  invitationId: string,
): Promise<{ audit_name: string; owner_name: string; voice_label: string }> {
  const res = await fetch(
    `${_API_ORIGIN}/api/roleforge/invitations/${encodeURIComponent(invitationId)}/public-meta`,
    { credentials: 'include' },
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(
      (body as Record<string, string>).code ?? 'http_error',
      res.status,
      (body as Record<string, string>).error ?? res.statusText,
    );
  }
  return res.json() as Promise<{ audit_name: string; owner_name: string; voice_label: string }>;
}

// ─── Phase 11 — Voices panel (owner-side thread browser) ───────────────────

/**
 * Summary row returned by `GET /api/roleforge/audits/:id/threads`. Contains
 * just enough info for the Voices tab to render one card per thread:
 *   - id / kind / voice_label / status — list semantics
 *   - contribution_counts — at-a-glance density per voice
 */
export interface ThreadSummary {
  id: string;
  kind: 'owner' | 'stakeholder';
  voice_label: string;
  status:
    | 'active'
    | 'completion_proposed'
    | 'completed_self'
    | 'completed_owner'
    | 'revoked';
  contribution_counts: {
    pain_points: number;
    opportunities: number;
  };
}

/**
 * Lists all voices contributing to an audit (owner thread first, then
 * stakeholders by created_at). Owner-side endpoint, Bearer auth.
 */
export async function listThreads(
  jwt: string,
  auditId: string,
): Promise<{ threads: ThreadSummary[] }> {
  const res = await fetch(
    `${API_BASE}/audits/${encodeURIComponent(auditId)}/threads`,
    {
      headers: { Authorization: `Bearer ${jwt}` },
    },
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(
      (body as Record<string, string>).code ?? 'http_error',
      res.status,
      (body as Record<string, string>).error ?? res.statusText,
    );
  }
  return res.json() as Promise<{ threads: ThreadSummary[] }>;
}

/**
 * Owner-only force-close on a stakeholder thread. Transitions the thread to
 * `completed_owner`, freezing further contributions while preserving the
 * stakeholder's read access.
 */
export async function forceCloseThread(
  jwt: string,
  threadId: string,
): Promise<{ ok: true }> {
  const res = await fetch(
    `${API_BASE}/threads/${encodeURIComponent(threadId)}/close`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${jwt}`,
        'Content-Type': 'application/json',
      },
    },
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(
      (body as Record<string, string>).code ?? 'http_error',
      res.status,
      (body as Record<string, string>).error ?? res.statusText,
    );
  }
  return res.json() as Promise<{ ok: true }>;
}

/**
 * Pain point as it appears inside roleforge_audits.metadata.pain_points,
 * filtered server-side to a single voice.
 */
export interface AttributedPainPoint {
  id: string;
  description: string;
  created_at: string;
}

/**
 * Returns per-thread attributed assets (pain points + opportunities). Powers
 * the AttributedAssetsPane in the middle column when the owner clicks a
 * voice in the Voices tab.
 */
export async function getAttributedAssets(
  jwt: string,
  threadId: string,
): Promise<{
  pain_points: AttributedPainPoint[];
  opportunities: Opportunity[];
}> {
  const res = await fetch(
    `${API_BASE}/threads/${encodeURIComponent(threadId)}/assets`,
    {
      headers: { Authorization: `Bearer ${jwt}` },
    },
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(
      (body as Record<string, string>).code ?? 'http_error',
      res.status,
      (body as Record<string, string>).error ?? res.statusText,
    );
  }
  return res.json() as Promise<{
    pain_points: AttributedPainPoint[];
    opportunities: Opportunity[];
  }>;
}

/**
 * Fetches the platform skill catalog from GET /api/skills/catalog.
 * Optionally filter by skill_type ('role' or 'tool').
 */
export async function fetchSkillCatalog(
  jwt: string,
  skill_type?: 'role' | 'tool',
): Promise<{ entries: SkillCatalogEntry[] }> {
  const qs = skill_type ? `?skill_type=${encodeURIComponent(skill_type)}` : '';
  const res = await fetch(`${SKILLS_API_BASE}/catalog${qs}`, {
    headers: {
      Authorization: `Bearer ${jwt}`,
      'Content-Type': 'application/json',
    },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(
      (body as Record<string, string>).code ?? 'http_error',
      res.status,
      (body as Record<string, string>).error ?? res.statusText,
    );
  }
  return res.json() as Promise<{ entries: SkillCatalogEntry[] }>;
}
